import React, { useState, useEffect, useContext } from "react";
import { API_URL } from "../components/api";
import Modal from "react-bootstrap/Modal";

import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridFooterContainer,
  GridPagination,
} from "@mui/x-data-grid";
import AuthContext from "../context/AuthContext";
import ExcelSVG from "../../ExcelSVG.svg";
import FilterSVG from "../../FilterSVG.svg";
import Button from "react-bootstrap/Button";
import * as XLSX from "xlsx";
// For API Requests
import axios from "axios";

function ReportingTextTable5min() {
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const { user } = useContext(AuthContext);

  const [filters, setFilters] = useState({
    subscription_mac_id: "",
    username: "",
    reseller: "",
    old_core: "",
    code: "",
    mac_address: "",
    description: "",
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({
    items: [],
  });

  const [searchInput, setSearchInput] = useState(""); // To capture search input

  // NEW: State to track column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [searchFilterTimeout, setSearchFilterTimeout] = useState(null);

  const authTokens = JSON.parse(localStorage.getItem("authTokens"));
  useEffect(() => {
    axios
      .get(`${API_URL}/radius/api/reporting/5min/`, {
        headers: { Authorization: `Bearer ${authTokens.access}` },
        params: { username: user.username },
      })
      .then((res) => setData(res.data.reverse()))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user.username, authTokens.access]);

  const columns = [
    { field: "subscription_mac_id", headerName: "ID", flex: 2, minWidth: 30 },
    { field: "username", headerName: "Username", flex: 2, minWidth: 100 },
    {
      field: "dealer",
      headerName: "Dealer",
      flex: 2,
      width: "auto",
      minWidth: 150,
      maxWidth: 150,
    },
    {
      field: "expected_code",
      headerName: "Expected",
      flex: 2,
      width: "auto",
      minWidth: 150,
      maxWidth: 150,
    },
    {
      field: "actual_code",
      headerName: "Actual",
      flex: 2,
      width: "auto",
      minWidth: 150,
      maxWidth: 150,
    },
    {
      field: "amount_removed",
      headerName: "Removed",
      flex: 2,
      width: "auto",
      minWidth: 150,
      maxWidth: 150,
    },
    {
      field: "old_credit",
      headerName: "Old Credit",
      flex: 2,
      width: "auto",
      minWidth: 150,
      maxWidth: 150,
    },
    {
      field: "new_credit",
      headerName: "New Credit",
      flex: 2,
      width: "auto",
      minWidth: 150,
      maxWidth: 150,
    },
    {
      field: "timestamp",
      headerName: "Date & Time",

      minWidth: 230,
      sortable: true,
      sort: "desc",
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return date.toLocaleString();
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 500,
      // Add these properties:
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "pre-wrap", // Preserve white spaces and line breaks
            wordBreak: "break-word", // Break words to prevent overflow
            lineHeight: "1.5", // Improve readability with better line height
            padding: "8px 0", // Add some vertical padding
            width: "100%", // Use full width of the cell
            height: "100%", // Use full height of the cell
            overflow: "auto", // Add scrollbar if needed
          }}
        >
          {params.value}
        </div>
      ),
    },
  ];

  const getRowId = (row) => row.subscription_mac_id;

  // Create a delayed search filter function
  const delaySearchFilter = (input) => {
    setSearchQuery(input);
  };

  useEffect(() => {
    // Save "searchValue" value before clearing other session storage items
    const searchValue = sessionStorage.getItem("searchReporting");

    // Clear all session storage items except "searchValue"
    Object.keys(sessionStorage).forEach((key) => {
      if (key !== "searchReporting") {
        sessionStorage.removeItem(key);
      }
    });

    // Restore "searchValue" value if needed
    if (searchValue) {
      sessionStorage.setItem("searchReporting", searchValue);
      setSearchQuery(searchValue);
    }
  }, []);

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchInput(query);
    sessionStorage.setItem("searchReporting", query);

    if (searchFilterTimeout) {
      clearTimeout(searchFilterTimeout);
    }

    const newTimeout = setTimeout(() => {
      delaySearchFilter(query);
    }, 100);
    setSearchFilterTimeout(newTimeout);
  };

  // IMPROVED: Combined filtering logic that works for both display AND export
  const filteredRows = data.filter((row) => {
    // Apply search query filter
    const searchMatch = searchQuery
      .split(" ")
      .every((word) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(word.toLowerCase().trim())
        )
      );

    // Apply modal filters
    const modalFilterMatch = Object.keys(filters).every((key) => {
      if (!filters[key]) return true;

      if (key === "connectivity_dependent") {
        const filterValue = filters[key].toLowerCase();
        if (row.connectivity === "Fiber") {
          return (row.code || "").toLowerCase().includes(filterValue);
        } else if (row.connectivity === "Wireless") {
          return (row.name || "").toLowerCase().includes(filterValue);
        }
        return false;
      }

      const rowValue = String(row[key] || "").toLowerCase();
      const filterValue = String(filters[key]).toLowerCase();
      return rowValue.includes(filterValue);
    });

    // Apply DataGrid filters
    const gridFilterMatch = gridFilterModel.items.every((filterItem) => {
      if (!filterItem.value) return true;

      const cellValue = row[filterItem.field];
      const filterValue = filterItem.value;

      switch (filterItem.operator) {
        case "contains":
          return String(cellValue || "")
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        case "equals":
          return String(cellValue || "") === String(filterValue);
        case "startsWith":
          return String(cellValue || "")
            .toLowerCase()
            .startsWith(String(filterValue).toLowerCase());
        case "endsWith":
          return String(cellValue || "")
            .toLowerCase()
            .endsWith(String(filterValue).toLowerCase());
        default:
          return true;
      }
    });

    return searchMatch && modalFilterMatch && gridFilterMatch;
  });

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const columnHeaders = {
    subscription_mac_id: "ID",
    username: "Username",
    dealer: "Dealer",
    expected_code: "Expected Code",
    actual_code: "Actual Code",
    amount_removed: "Amount Removed",
    old_credit: "Old Credit",
    new_credit: "New Credit",
    timestamp: "Date",
    description: "Description",
  };

  // Format date fields properly for export — e.g. "21/02/2025 10:25:52 AM"
  const formatDateForExport = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);

    // Format as DD/MM/YYYY HH:MM:SS AM/PM
    const formattedDate = date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    // Ensure consistent spacing before AM/PM
    return formattedDate.replace(" am", " AM").replace(" pm", " PM");
  };

  /// Corrected: Transform rows respecting column visibility
  const transformRowsForExport = (rows) => {
    return rows.map((row) => {
      const transformedRow = {};

      Object.keys(columnHeaders).forEach((field) => {
        // Only export if column is visible
        if (columnVisibilityModel[field] !== false) {
          const headerName = columnHeaders[field];

          // Default: use the value or empty string
          transformedRow[headerName] = row[field] ?? "";
        }
      });

      return transformedRow;
    });
  };

  // IMPROVED: Export function that uses the already-filtered rows
  const handleExportToExcel = () => {
    console.log("Exporting data...");
    console.log("Total data rows:", data.length);
    console.log("Filtered rows:", filteredRows.length);
    console.log("Active filters:", {
      search: searchQuery,
      modalFilters: filters,
      gridFilters: gridFilterModel.items,
    });
    console.log("Column visibility:", columnVisibilityModel);

    // Always use filteredRows which has ALL filters applied
    const transformedRows = transformRowsForExport(filteredRows);

    console.log("Rows to export:", transformedRows.length);

    const worksheet = XLSX.utils.json_to_sheet(transformedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subscription Mac");
    XLSX.writeFile(workbook, "Subscription Mac.xlsx");
  };

  const [isPressed, setIsPressed] = useState(false);

  const handleExportClick = () => {
    setIsPressed(true);
    handleExportToExcel();
    setTimeout(() => {
      setIsPressed(false);
    }, 200);
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <Button
        onClick={() => setShowFilterModal(true)}
        variant="outline-primary"
        style={{
          border: "none",
        }}
      >
        <img
          src={FilterSVG}
          alt="Filter Icon"
          style={{
            color: "white",
            width: "18px",
            marginRight: "8px",
            filter: "invert(0)", // keep black icon
          }}
        />
        Filter
      </Button>
      {/*<GridToolbarFilterButton />*/}
      <GridToolbarExport />
      <Button
        onClick={handleExportClick}
        variant="outline-success"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "5px 10px",
          fontSize: "14px",
          border: "none",
          backgroundColor: isPressed ? "white" : "transparent",
          color: isPressed ? "black" : "#007bff",
          cursor: "pointer",
        }}
      >
        <img
          src={ExcelSVG}
          alt="Excel Icon"
          style={{
            width: "20px",
            marginRight: "8px",
            filter: isPressed ? "invert(0)" : "invert(1)",
          }}
        />
        Excel
      </Button>
    </GridToolbarContainer>
  );

  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          padding: "10px",
        }}
      >
        <GridPagination />
      </div>
    </GridFooterContainer>
  );

  return (
    <div className="ClientTable">
      <style>{`
        .MuiDataGrid-panelWrapper {
          background: #122632 !important;
        }
      `}</style>
      <input
        type="text"
        placeholder="Search..."
        value={searchInput}
        onChange={handleSearchChange}
        className="search-client"
      />
      {!loading && data.length > 0 && (
        <div style={{ height: 520, width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={getRowId}
            filterModel={gridFilterModel}
            onFilterModelChange={(newModel) => setGridFilterModel(newModel)}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            rowsPerPageOptions={[10, 100, 1000]}
            pagination
            disableSelectionOnClick
            getRowHeight={() => 80} // Set a fixed row height
            headerHeight={56} // Optional: Adjust header height
            components={{
              Toolbar: CustomToolbar,
            }}
            sx={{
              "& .MuiDataGrid-row": {
                minHeight: "80px !important", // Ensure minimum row height
                alignItems: "center",
              },
              "& .MuiDataGrid-cell": {
                padding: "8px",
                alignItems: "center",
              },
            }}
            sortModel={[
              {
                field: "creation_date",
                sort: "desc", // Sort by 'creation_date' in descending order
              },
            ]}
          />
          <Modal
            show={showFilterModal}
            onHide={() => setShowFilterModal(false)}
          >
            <Modal.Header closeButton>
              <Modal.Title>Filters</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {columns
                .filter((col) => col.field !== "actions")
                .map((col) => (
                  <div key={col.field} className="filter">
                    <label>{col.headerName}</label>
                    <input
                      type="text"
                      placeholder={`Filter by ${col.headerName}`}
                      value={filters[col.field] || ""}
                      onChange={(e) =>
                        handleFilterChange(col.field, e.target.value)
                      }
                      className="filter-input"
                    />
                  </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowFilterModal(false)}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </div>
  );
}

export default ReportingTextTable5min;
